import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LESSON } from "@/constants/testIds";
import { Check, Download, Building2, ArrowRight, ArrowLeft, ClipboardCheck, Clock, ExternalLink } from "lucide-react";
import Watermark from "@/components/Watermark";
import SmartVideoPlayer from "@/components/SmartVideoPlayer";
import { PremiumLessonStop } from "@/components/PremiumLessonStop";

// Manual "Mark complete" gates. Users must have EITHER (a) sat on the
// lesson for at least 30 seconds OR (b) played at least 2 minutes of
// video before the button is enabled — enough friction that a click-
// through speedrunner can't fake progress in seconds, but light enough
// that an experienced user who genuinely knows the material isn't stuck.
const TIME_GATE_SECONDS = 30;
const VIDEO_GATE_SECONDS = 120;

export default function LessonPlayer() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState("");
  const [premiumStop, setPremiumStop] = useState(false);
  const [completed, setCompleted] = useState(false);
  // Track dwell-time + video-play-time locally so the manual complete
  // gate has something to check without hammering the backend.
  const [dwellSec, setDwellSec] = useState(0);
  const [videoSec, setVideoSec] = useState(0);
  // Highwater mark of continuously-reached playback seconds. Guards
  // against the "scrub to the end and pause" cheat.
  const highwaterRef = useRef(0);

  useEffect(() => {
    setLesson(null); setCompleted(false); setDwellSec(0); setVideoSec(0); setPremiumStop(false);
    highwaterRef.current = 0;
    api.get(`/lessons/${id}`)
      .then((r) => {
        setLesson(r.data);
        // Preload completion state so Next/Quiz is unlocked immediately
        // on refresh of an already-finished lesson.
        if (r.data.completed) setCompleted(true);
      })
      .catch((e) => {
        setError(formatApiError(e.response?.data?.detail) || t("lessonPlayerUi.loadError", { message: e.message }));
      });
  }, [id]);

  // 1-second dwell-timer while the page is mounted.
  useEffect(() => {
    if (!lesson) return;
    const iv = setInterval(() => setDwellSec((s) => s + 1), 1000);
    return () => clearInterval(iv);
  }, [lesson]);

  // The player reports absolute `playedSeconds`. Scrubbing backward
  // doesn't retroactively re-lock the button, scrubbing forward
  // doesn't cheat past the gate — see `onVideoPlaying` below.
  const onVideoPlaying = (playedSeconds) => {
    // Guard against a scrub-past-then-back sequence: only credit the
    // gate when the reported time is within a reasonable delta of the
    // current highwater (i.e. actual playback, not a seek).
    const prev = highwaterRef.current;
    if (playedSeconds > prev && playedSeconds - prev < 5) {
      highwaterRef.current = playedSeconds;
      setVideoSec(playedSeconds);
    }
    // Big forward jump = scrub, don't count. Backward = no change.
  };
  const onVideoPause = () => { /* highwater persists — no reset */ };

  const gateOpen = dwellSec >= TIME_GATE_SECONDS || videoSec >= VIDEO_GATE_SECONDS;
  const gateSummary = (() => {
    if (gateOpen) return null;
    const dwellLeft = Math.max(0, TIME_GATE_SECONDS - Math.floor(dwellSec));
    const videoLeft = Math.max(0, VIDEO_GATE_SECONDS - Math.floor(videoSec));
    return t("lessonPlayerUi.gateHint", { dwellLeft, videoMin: Math.floor(videoLeft / 60), videoSec: videoLeft % 60 });
  })();

  const markComplete = async () => {
    try {
      // Send the local dwell + video-play highwaters so the backend
      // can verify the gate server-side (defense-in-depth against
      // DevTools bypass of the client button-disabled state).
      await api.post(`/progress/${id}`, {
        dwell_sec: Math.floor(dwellSec),
        video_sec: Math.floor(videoSec),
      });
      setCompleted(true);
      toast.success(t("lessonPlayerUi.lessonComplete"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("lessonPlayerUi.markCompleteError", { message: e.message }));
    }
  };

  const goPrev = () => {
    if (lesson?.nav?.prev_lesson_id) navigate(`/lessons/${lesson.nav.prev_lesson_id}`);
  };
  const goNext = () => {
    const n = lesson?.nav?.next;
    if (!n) return;
    if (n.kind === "premium") setPremiumStop(true);
    else if (n.kind === "quiz") navigate(`/quizzes/${n.id}`);
    else navigate(`/lessons/${n.id}`);
  };

  if (error) {
    return (
      <div className="text-slate-600">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-6">
          {error}
        </div>
        <Button onClick={() => navigate("/pricing")} className="mt-4 bg-blue-800 text-white">
          {t("lessonPlayerUi.viewMemberships")}
        </Button>
      </div>
    );
  }

  if (!lesson) {
    return <div className="text-slate-500">{t("lessonPlayerUi.loadingLesson")}</div>;
  }

  const nav = lesson.nav || {};
  const nextIsQuiz = nav.next?.kind === "quiz";

  return (
    <div data-testid={LESSON.root} className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-2">
          {t("lessonPlayerUi.lessonDuration", { duration: lesson.duration })}
        </div>
        <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight">
          {lesson.title}
        </h1>
      </div>

      <div className="bg-slate-900 rounded-2xl overflow-hidden aspect-video">
        <Watermark>
          <SmartVideoPlayer
            key={lesson.id}
            testId={LESSON.video}
            url={lesson.video_url}
            onProgress={onVideoPlaying}
            onPause={onVideoPause}
          />
        </Watermark>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <Button
          data-testid={LESSON.markComplete}
          onClick={markComplete}
          disabled={completed || !gateOpen}
          className="bg-blue-800 hover:bg-blue-700 text-white h-11 px-6 disabled:opacity-50"
          title={gateSummary || undefined}
        >
          <Check size={16} className="mr-2" />
          {completed ? t("lessonPlayerUi.completed") : t("lessonPlayerUi.markAsComplete")}
        </Button>
        {!gateOpen && !completed && (
          <div className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="lesson-gate-hint">
            <Clock size={13} /> {gateSummary}
          </div>
        )}
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger data-testid={LESSON.tabsOverview} value="overview">{t("lessonPlayerUi.tabOverview")}</TabsTrigger>
          <TabsTrigger data-testid={LESSON.tabsTranscript} value="transcript">{t("lessonPlayerUi.tabTranscript")}</TabsTrigger>
          <TabsTrigger data-testid={LESSON.tabsResources} value="resources">{t("lessonPlayerUi.tabResources")}</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6">
          <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{lesson.overview}</p>
        </TabsContent>
        <TabsContent value="transcript" className="mt-6">
          <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
            {lesson.transcript}
          </div>
        </TabsContent>
        <TabsContent value="resources" className="mt-6 space-y-4">
          {(lesson.attachments || []).map((a, i) => {
            const href = a.url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${a.url}` : a.url;
            return (
              <a
                key={`${a.url}-${i}`}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={`lesson-attachment-${i}`}
                className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4 hover:border-blue-300 hover:shadow-sm transition-[border-color,box-shadow]"
              >
                {a.kind === "link" ? <ExternalLink className="text-blue-800 shrink-0" size={20} /> : <Download className="text-blue-800 shrink-0" size={20} />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{a.title}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest">
                    {a.kind === "link" ? t("lessonPlayerUi.attachmentLink") : `${(a.filename || "").split(".").pop()} · ${a.size > 1048576 ? `${(a.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((a.size || 0) / 1024))} KB`}`}
                  </div>
                </div>
                <span className="text-sm font-semibold text-blue-800">{a.kind === "link" ? t("lessonPlayerUi.open") : t("lessonPlayerUi.download")}</span>
              </a>
            );
          })}

          <Link
            to="/resources?tab=brokers"
            data-testid={LESSON.brokerCta}
            className="group block bg-gradient-to-br from-slate-950 to-blue-950 text-white rounded-xl p-6 border border-amber-500/25 relative overflow-hidden"
          >
            <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-amber-500/15 blur-3xl" />
            <div className="relative flex items-center gap-5">
              <div className="w-11 h-11 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
                <Building2 size={20} className="text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold">
                  {t("lessonPlayerUi.readyToTrade")}
                </div>
                <div className="font-display font-bold text-lg mt-1">
                  {t("lessonPlayerUi.openBroker")}
                </div>
                <div className="text-sm text-slate-300 mt-1">
                  {t("lessonPlayerUi.brokerPerks")}
                </div>
              </div>
              <ArrowRight size={18} className="text-amber-400 shrink-0 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </TabsContent>
      </Tabs>

      {/* Sequential nav: Prev + Next (or the chapter quiz when this is
          the last lesson of its chapter). Keeps learners in-flow so
          they never have to reach for the browser back button. */}
      <div className="pt-6 mt-2 border-t border-slate-200 flex items-center justify-between gap-4">
        <Button
          data-testid="lesson-nav-prev"
          onClick={goPrev}
          disabled={!nav.prev_lesson_id}
          variant="outline"
          className="h-11 gap-2 disabled:opacity-40"
        >
          <ArrowLeft size={16} /> {t("lessonPlayerUi.previousLesson")}
        </Button>
        {nav.next ? (
          <Button
            data-testid={nextIsQuiz ? "lesson-nav-quiz" : "lesson-nav-next"}
            onClick={goNext}
            disabled={!completed}
            title={!completed
              ? t("lessonPlayerUi.markLessonFirst")
              : (nextIsQuiz ? t("lessonPlayerUi.takeQuiz") : t("lessonPlayerUi.continue"))}
            className={`h-11 gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${nextIsQuiz ? "bg-amber-500 hover:bg-amber-600 text-slate-900" : "bg-blue-800 hover:bg-blue-700 text-white"}`}
          >
            {nextIsQuiz ? (<><ClipboardCheck size={16} /> {t("lessonPlayerUi.takeChapterQuiz")}</>) :
              (<>{t("lessonPlayerUi.nextLesson")} <ArrowRight size={16} /></>)}
          </Button>
        ) : null}
        {nav.next?.kind === "premium" && <PremiumLessonStop next={nav.next} open={premiumStop} onOpenChange={setPremiumStop} />}
        {nav.next ? null : (
          <div className="text-sm text-slate-500 italic">{t("lessonPlayerUi.endOfChapter")}</div>
        )}
      </div>
      {!completed && nav.next && (
        <p
          data-testid="lesson-nav-locked-hint"
          className="mt-2 text-xs text-slate-500 text-right"
        >
          <span className="inline-flex items-center gap-1">
            <Clock size={11} />
            {nextIsQuiz
              ? t("lessonPlayerUi.quizUnlockHint")
              : t("lessonPlayerUi.nextUnlockHint")}
          </span>
        </p>
      )}
    </div>
  );
}
